import type {
  AttributeBoundary,
  GridLabel,
  GridType,
  ICanvasSize,
  MapHeader,
  Nullable,
  PaletteBody,
  Point2D,
  Range2D,
  ScaleType,
} from '@library/types'
import {
  applyMatToVec2,
  getCoordsToCanvasMatrix,
  getGlobalToLocalMatrix,
  isCanvasSizeNotZero,
} from '@library/utils'
import { bboxPolygon } from '@turf/bbox-polygon'
import { booleanIntersects } from '@turf/boolean-intersects'
import type { Quadtree } from 'd3'
import { quadtree } from 'd3'
import { mat3, vec4 } from 'gl-matrix'

import { findContour } from './findContour'

/*  Алгоритм распределения надписи.
 *  Исходные данные: карта mapBody - в виде одномерного мессива, на самом деле представляет двумерынй массив
 *  размером (MapHeader.columns x MapHeader.rows)
 *
 *  Карту разбиваем на прямоугольники, в результате получаем массив прямоугольников
 *  LABELS_COUNT_PER_DIMENSION х LABELS_COUNT_PER_DIMENSION
 *  Для каждого прямоугольника находим первую попавшуюся надпись.
 * */

const LABELS_COUNT_PER_DIMENSION = 200
const MAX_SIZE_TREE_NODE = 300
const MIN_SIZE_TREE_NODE = 150
const MIN_DISTANCE_BETWEEN_TWO_LABELS = 150

const calcIsolineForTwoZ = (isolineBase: number, isolineStep: number, z1: number, z2: number) => {
  const minZ = Math.min(z1, z2)
  const maxZ = Math.max(z1, z2)
  const kk = Math.floor((minZ - isolineBase) / isolineStep)
  const remainder = (minZ - isolineBase) % isolineStep
  const isolineValue = remainder === 0 ? minZ : (kk + 1) * isolineStep + isolineBase
  const found = minZ <= isolineValue && isolineValue < maxZ
  let part = found ? (isolineValue - minZ) / (maxZ - minZ) : 0
  part = z1 > z2 ? 1 - part : part
  return { found, part, isolineValue }
}

type GetLabelCoordinate = {
  found: boolean
  x: number
  y: number
  z: number
  atan: number
}

/* Вычисляет угол наклона надписи по трем точкам */
const calcLabelAtan = (z00: number, z01: number, z10: number): number => {
  let atan = Math.atan((z01 - z00) / (z10 - z00))
  atan = (atan * 180) / Math.PI

  return atan
}

const getLabelCoordinate = (
  mapHeader: MapHeader,
  mapBody: GridType['body'],
  isolineBase: number,
  isolineStep: number,
  col1: number,
  row1: number,
): GetLabelCoordinate => {
  const col2 = col1 + 1
  const row2 = row1 + 1
  const [x0, y0] = [
    mapHeader.minX + mapHeader.stepX * col1,
    mapHeader.minY + mapHeader.stepY * row1,
  ]

  const [z00, z01, z10, z11] = [
    mapBody[mapHeader.columns * row1 + col1],
    mapBody[mapHeader.columns * row1 + col2],
    mapBody[mapHeader.columns * row2 + col1],
    mapBody[mapHeader.columns * row2 + col2],
  ]

  const null_value = mapHeader.nullValue
  if (z00 === null_value || z01 === null_value || z10 === null_value || z11 === null_value) {
    return { found: false, x: 0, y: 0, z: 0, atan: 0 }
  }

  let aa = calcIsolineForTwoZ(isolineBase, isolineStep, z00, z01)
  if (aa.found) {
    return {
      found: true,
      x: x0 + mapHeader.stepX * aa.part,
      y: y0,
      z: aa.isolineValue,
      atan: calcLabelAtan(z00, z01, z10),
    }
  }

  aa = calcIsolineForTwoZ(isolineBase, isolineStep, z00, z10)
  if (aa.found) {
    return {
      found: true,
      x: x0,
      y: y0 + mapHeader.stepY * aa.part,
      z: aa.isolineValue,
      atan: calcLabelAtan(z00, z01, z10),
    }
  }

  return { found: false, x: 0, y: 0, z: 0, atan: 0 }
}

const getMapContourQuadTree = (
  mapHeader: MapHeader,
  mapBody: GridType['body'],
): Quadtree<Point2D> => {
  const mapContour = findContour(mapBody, mapHeader.columns, mapHeader.rows, mapHeader.nullValue)
  return quadtree<Point2D>(
    mapContour,
    point => mapHeader.minX + mapHeader.stepX * point.x,
    point => mapHeader.minY + mapHeader.stepY * point.y,
  )
}

export const getIsolineLabels = (
  mapHeader: MapHeader,
  mapBody: GridType['body'],
  isolineBase: number,
  isolineStep: number,
  palette: PaletteBody,
  attributeBoundary?: AttributeBoundary,
): GridLabel[] => {
  const labels: GridLabel[] = []
  const columnsPerStep = Math.ceil(mapHeader.columns / LABELS_COUNT_PER_DIMENSION)
  const rowsPerStep = Math.ceil(mapHeader.rows / LABELS_COUNT_PER_DIMENSION)
  const mapContourQuadTree = getMapContourQuadTree(mapHeader, mapBody)

  for (
    let iColumnStep = 2 * columnsPerStep;
    iColumnStep < mapHeader.columns - 2 * columnsPerStep + 1;
    iColumnStep += 2 * columnsPerStep
  ) {
    for (
      let iRowStep = 2 * rowsPerStep;
      iRowStep < mapHeader.rows - 2 * rowsPerStep + 1;
      iRowStep += 2 * rowsPerStep
    ) {
      let keepContinue = true
      for (let iColumn = 0; keepContinue && iColumn < columnsPerStep; iColumn++) {
        for (let iRow = 0; keepContinue && iRow < rowsPerStep; iRow++) {
          const label = getLabelCoordinate(
            mapHeader,
            mapBody,
            isolineBase,
            isolineStep,
            iColumnStep + iColumn,
            iRowStep + iRow,
          )
          if (label.found) {
            const nullCell = mapContourQuadTree.find(label.x, label.y)

            labels.push({
              atan: label.atan,
              backgroundColor: getColorForZ(mapHeader, label.z, palette, attributeBoundary),
              x: label.x,
              y: label.y,
              z: label.z,
              canvasX: 0,
              canvasY: 0,
              nearestNullValueX: nullCell ? mapHeader.minX + mapHeader.stepX * nullCell.x : 0,
              nearestNullValueY: nullCell ? mapHeader.minY + mapHeader.stepY * nullCell.y : 0,
            })
            keepContinue = false
          }
        }
      }
    }
  }
  return labels
}

export const getSparsedPoints = (
  canvas: Nullable<ICanvasSize>,
  origin: Range2D,
  zoom: ScaleType,
  tree: Quadtree<GridLabel>,
) => {
  if (!canvas || !isCanvasSizeNotZero(canvas)) return []

  const globalToCanvasMatrix = getGlobalToLocalMatrix(
    getCoordsToCanvasMatrix(mat3.create(), canvas, zoom),
    origin,
  )
  const canvasRect = bboxPolygon([0, 0, canvas.width, canvas.height])
  const points: GridLabel[] = []
  tree.visit((node, x0, y0, x1, y1) => {
    const canvasPoint0 = applyMatToVec2(globalToCanvasMatrix, { x: x0, y: y0 })
    const canvasPoint1 = applyMatToVec2(globalToCanvasMatrix, { x: x1, y: y1 })
    const rect = bboxPolygon([canvasPoint0.x, canvasPoint0.y, canvasPoint1.x, canvasPoint1.y])
    if (!booleanIntersects(rect, canvasRect)) return true

    let stopVisiting = false
    let labelToAdd: GridLabel | undefined = undefined
    if (node.length) {
      const nodeWidth = canvasPoint1.x - canvasPoint0.x

      if (nodeWidth < MAX_SIZE_TREE_NODE && nodeWidth > MIN_SIZE_TREE_NODE) {
        const xMean = (x0 + x1) / 2
        const yMean = (y0 + y1) / 2
        labelToAdd = tree.find(xMean, yMean)

        stopVisiting = true
      }
    } else {
      labelToAdd = node.data
    }

    if (labelToAdd) {
      const canvasCoord = applyMatToVec2(globalToCanvasMatrix, {
        x: labelToAdd.x,
        y: labelToAdd.y,
      })
      labelToAdd.canvasX = canvasCoord.x
      labelToAdd.canvasY = canvasCoord.y

      const canvasNearestNull = applyMatToVec2(globalToCanvasMatrix, {
        x: labelToAdd.nearestNullValueX,
        y: labelToAdd.nearestNullValueY,
      })

      if (
        !(
          (Math.abs(canvasNearestNull.x - labelToAdd.canvasX) < 50 &&
            Math.abs(canvasNearestNull.y - labelToAdd.canvasY) < 50) ||
          points.some(
            label =>
              Math.abs(label.canvasX - labelToAdd.canvasX) < MIN_DISTANCE_BETWEEN_TWO_LABELS &&
              Math.abs(label.canvasY - labelToAdd.canvasY) < MIN_DISTANCE_BETWEEN_TWO_LABELS,
          )
        )
      ) {
        points.push(labelToAdd)
      }
    }

    return stopVisiting
  })

  return points
}

const constructZValueToPalettePositionConverter = (maxZ: number, lenZ: number) => (z: number) => {
  const val255 = ((maxZ - z) / lenZ) * 255
  return Math.min(255, val255 < 0 ? 0 : Math.trunc(val255)) * 4
}

export const getColorForZ = (
  mapHeader: MapHeader,
  valueZ: number,
  chosenPalette: PaletteBody,
  attributeBoundary?: AttributeBoundary,
): vec4 => {
  const start = attributeBoundary?.start ?? mapHeader.minZ
  const stop = attributeBoundary?.stop ?? mapHeader.maxZ
  const lenZ = stop - start

  const convertZValueToPalettePosition = constructZValueToPalettePositionConverter(stop, lenZ)

  return getColorFromPallete(convertZValueToPalettePosition, chosenPalette, valueZ)
}

const getColorFromPallete = (
  converter: (value: number) => number,
  palette: PaletteBody,
  z: number,
): vec4 => {
  const pos = converter(z)

  const cr = palette[pos + 0] / 255
  const cg = palette[pos + 1] / 255
  const cb = palette[pos + 2] / 255
  const ca = palette[pos + 3] / 255
  return vec4.fromValues(cr, cg, cb, ca)
}
